import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Panel } from '../Panel.tsx';
import { DataTable, type Column, type SortState } from '../DataTable.tsx';
import { StatusDot } from '../StatusDot.tsx';
import { duration, midEllipsis, NO_VALUE, num } from '../format.ts';
import { levelTone, matrixTone, STATUS_STATE } from '../state.ts';
import { FaultLine } from '../states/Faults.tsx';
import { figure, unread, type Read } from '../states/read.ts';
import type { RobotStatus } from '../../hooks/useRobots.ts';
import type { TelemetryReading } from '../../hooks/useTelemetry.ts';
import {
  AXIS_LABEL, readingLevel, readingNum, readingStr, stampOf,
  type Channel, type TimeAxis,
} from './channels.ts';
import { useCursor } from './cursor.ts';
import { CursorStamp } from './CursorStamp.tsx';
import type { ChartSeries } from './TimeSeriesCard.tsx';
import styles from './charts.module.css';

/* ==========================================================================
   The ledger — every reading in the window, exactly as the agent sent it.

   It is the same rows the stack above it is drawing, and it is bound to the
   same window and the same cursor: the trace says WHEN something happened,
   the row says WHAT ELSE was true at that instant. Until the two are bound
   an operator matches timestamps by eye, which is the whole reason both are
   on one screen.

     · the range is the transport's, never a row budget. `100 | 300 | 600`
       is a control that means eight hours on a 2m24s robot and half an hour
       on a 10s one — the same button producing two different questions.
     · hovering a trace highlights the row; clicking a row pins the playhead
       and every plot on the surface moves to it.
     · the numeric columns are pinned narrow. A six-character enum has no
       business holding 156px, and the width it gives back is width the
       instrument spends on time resolution.
     · nothing is rounded past the precision the wire carried, and a null is
       an em dash — never a zero.
   ========================================================================== */

/** Rows past this are not rendered. A window can hold more than a DOM should. */
const MAX_ROWS = 800;

interface LedgerRow {
  key: string;
  robot: string;
  t: number;
  r: TelemetryReading;
}

const P2 = (n: number) => String(n).padStart(2, '0');

function zulu(t: number, withDay: boolean): string {
  const d = new Date(t);
  const time = `${P2(d.getUTCHours())}:${P2(d.getUTCMinutes())}:${P2(d.getUTCSeconds())}Z`;
  return withDay
    ? `${P2(d.getUTCMonth() + 1)}-${P2(d.getUTCDate())} ${time}`
    : time;
}

export function ReadingsLedger({
  series,
  channels,
  axis,
  loading = false,
  tools,
  read,
  robotNames,
}: {
  series: readonly ChartSeries[];
  /** The channels currently on the instrument — the ledger shows the same. */
  channels: readonly Channel[];
  axis: TimeAxis;
  loading?: boolean | undefined;
  tools?: ReactNode;
  /**
   * THE READ THE ROWS CAME FROM. Required: `Readings (0)` and `No readings in
   * this window.` are both counted from `series`, which is `[]` on a quiet
   * window and `[]` on a dead endpoint. The count becomes `—` and the line
   * becomes `Reading…` / `Nothing has been read.`, and the panel grows the
   * fault line it owed — this panel used to carry neither, so a failed
   * telemetry read read as a window with nothing in it.
   */
  read: Read;
  /** robot_id → display name, for the comparison column. */
  robotNames?: ReadonlyMap<string, string> | undefined;
}) {
  const cursor = useCursor();
  const multi = series.length > 1;
  /* Newest first is the resting order — the question a ledger is usually open
     for is "what just happened". Reversing it follows a replay instead, which
     is why the control exists at all rather than the order being a constant. */
  const [sort, setSort] = useState<SortState>({ key: 'ts', dir: 'desc' });

  const rows = useMemo<LedgerRow[]>(() => {
    const out: LedgerRow[] = [];
    for (const s of series) {
      for (const r of s.readings) {
        const t = stampOf(r, axis);
        if (!Number.isFinite(t) || t < cursor.from || t > cursor.to) continue;
        out.push({ key: `${s.id}#${r.id}`, robot: s.id, t, r });
      }
    }
    out.sort((a, b) => (sort.dir === 'desc' ? b.t - a.t : a.t - b.t));
    return out;
  }, [series, axis, cursor.from, cursor.to, sort.dir]);

  const shown = rows.length > MAX_ROWS ? rows.slice(0, MAX_ROWS) : rows;

  /* Median interval between the rows on screen — the ROBOT's sample rate,
     which is a different clock from how often the console polls. */
  const cadence = useMemo(() => {
    const t = series[0]?.readings
      .map((r) => stampOf(r, axis))
      .filter((v) => Number.isFinite(v) && v >= cursor.from && v <= cursor.to)
      .sort((a, b) => a - b) ?? [];
    if (t.length < 2) return 0;
    const d: number[] = [];
    for (let i = 1; i < t.length; i += 1) d.push(t[i]! - t[i - 1]!);
    d.sort((a, b) => a - b);
    return d[d.length >> 1] ?? 0;
  }, [series, axis, cursor.from, cursor.to]);

  /* The row the cursor is standing on. Same tolerance rule as every other
     readout in the kit: past one cadence there is no row at this instant, so
     nothing is marked rather than the nearest one being marked wrongly. */
  const currentKey = useMemo(() => {
    if (cursor.at === null || shown.length === 0) return null;
    const tol = cadence > 0 ? cadence : 60_000;
    let best: LedgerRow | null = null;
    let bestD = Infinity;
    for (const row of shown) {
      if (multi && row.robot !== series[0]?.id) continue;
      const d = Math.abs(row.t - cursor.at);
      if (d < bestD) { bestD = d; best = row; }
    }
    return best !== null && bestD <= tol ? best.key : null;
  }, [shown, cursor.at, cadence, multi, series]);

  /* THE PANE FOLLOWS THE CROSSHAIR.

     This used to follow the PLAYHEAD only, on the argument that scrolling
     under a sweeping pointer makes a list unreadable. That was the wrong
     trade: eight rows out of a hundred and fifty, pinned to the newest, is a
     pane that answers a question nobody asked while the operator is holding
     the cursor over the instant they care about. The row IS the crosshair's
     numeric readout — every field the lanes do not plot, at the instant the
     lanes are quoting — and a readout that has to be scrolled to is not one.

     What keeps it readable is the band rather than the pin: the row is moved
     only when it leaves the middle of the pane, and then only far enough to
     put one row of context on the side it left by. So a slow sweep walks the
     list a row at a time with its neighbours visible, and a jump across the
     window lands with the row in view instead of at the edge.

     The scroller is DataTable's, found by walking up from the row: this
     panel does not own that element and must not assume where it is. Nothing
     above it is touched — `scrollIntoView` would have scrolled the whole
     workspace to satisfy a hover. */
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /* The effect below turns on WHICH row and WHETHER a crosshair is up — never
     on the instant itself, or a sweep across one row would re-measure the
     pane sixty times a second to decide it had nothing to do. */
  const cursorLive = cursor.at !== null;
  const scrollerOf = (host: HTMLElement, from: HTMLElement): HTMLElement | null => {
    let el: HTMLElement | null = from;
    while (el !== null && el !== host.parentElement) {
      if (el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    return null;
  };
  useEffect(() => {
    const host = wrapRef.current;
    if (host === null) return;

    /* THE CURSOR LET GO — the pane goes back to the newest, because that is
       what its stamp now says it is reading. A list left three hours down
       under a header that reads NEWEST is the two-clocks bug in one panel. */
    if (currentKey === null) {
      /* A crosshair over a coverage hole has no row to mark, but it has not
         been put away either — yanking the list back to the newest while the
         operator is still scrubbing would throw away the place they were
         reading to answer a question they did not ask. Hold position; the
         stamp already says `no reading here`. */
      if (cursorLive) return;
      const table = host.querySelector('table');
      const idle = table === null ? null : scrollerOf(host, table);
      if (idle !== null) idle.scrollTop = sort.dir === 'desc' ? 0 : idle.scrollHeight;
      return;
    }

    const row = host.querySelector('[data-current="true"]');
    if (!(row instanceof HTMLElement) || row.parentElement === null) return;
    const scroller = scrollerOf(host, row.parentElement);
    if (scroller === null) return;

    const head = scroller.querySelector('thead');
    const headH = head === null ? 0 : head.getBoundingClientRect().height;
    const cr = scroller.getBoundingClientRect();
    const rr = row.getBoundingClientRect();
    const rowH = rr.height > 0 ? rr.height : 26;
    const viewTop = cr.top + headH;
    const viewH = Math.max(1, cr.bottom - viewTop);
    /* One row of context on whichever side the cursor left by, so a sweep
       walks the list rather than pinning the row to an edge. */
    let delta = 0;
    if (rr.top < viewTop + rowH) delta = rr.top - (viewTop + rowH);
    else if (rr.bottom > cr.bottom - rowH) delta = rr.bottom - (cr.bottom - rowH);
    if (delta === 0) return;
    /* A move longer than the pane is a JUMP, not a step — the operator
       clicked somewhere else on the scrub bar. Land it in the middle, where
       both its neighbourhoods are readable, instead of hard against an edge
       with the direction of travel as the only clue where the rest went. */
    if (Math.abs(delta) > viewH) delta = rr.top + rowH / 2 - (viewTop + viewH / 2);
    /* Snapped to the row grid: a 240px pane showing seven rows and two halves
       is a pane whose top line is unreadable, and the top line is the one the
       eye lands on. */
    scroller.scrollTop = Math.round((scroller.scrollTop + delta) / rowH) * rowH;
  }, [currentKey, cursorLive, sort.dir]);

  /* The newest row in the window, for the stamp below — what the pane is
     reading when nobody is pointing at anything. */
  const newestT = useMemo(() => {
    let m: number | null = null;
    for (const row of shown) if (m === null || row.t > m) m = row.t;
    return m;
  }, [shown]);

  const boots = useMemo(() => {
    const seen = new Set<string>();
    for (const row of shown) {
      const b = readingStr(row.r, 'agent_boot_id');
      if (b !== null) seen.add(b);
    }
    return seen.size;
  }, [shown]);

  const crossesDay = useMemo(() => {
    const a = new Date(cursor.from);
    const b = new Date(cursor.to);
    return a.getUTCDate() !== b.getUTCDate() || a.getUTCMonth() !== b.getUTCMonth();
  }, [cursor.from, cursor.to]);

  const columns = useMemo<Column<LedgerRow>[]>(() => {
    const out: Column<LedgerRow>[] = [
      {
        key: 'ts',
        header: axis === 'agent' ? 'Capture time' : 'Ingest time',
        kind: 'time',
        width: crossesDay ? '19ch' : '14ch',
        mono: true,
        sortable: true,
        cell: (row) => zulu(row.t, crossesDay),
      },
    ];
    if (multi) {
      out.push({
        key: 'robot',
        header: 'Robot',
        kind: 'id',
        width: '15ch',
        mono: true,
        cell: (row) => robotNames?.get(row.robot) ?? row.robot,
      });
    }
    out.push({
      key: 'state',
      header: 'Mode',
      kind: 'status',
      /* Pinned. `active` is six characters and a status column that grows
         with the monitor is the dead air the instrument wanted. */
      width: '12ch',
      cell: (row) => {
        const s = row.r.state;
        const d = s !== null && s in STATUS_STATE ? STATUS_STATE[s as RobotStatus] : null;
        if (d === null) return NO_VALUE;
        return (
          <span className={styles.ledgerMode}>
            <StatusDot tone={matrixTone(d.tone)} hollow={d.hollow} />
            {d.label}
          </span>
        );
      },
    });

    for (const c of channels) {
      out.push({
        key: c.key,
        header: c.label,
        kind: 'num',
        unit: c.unit,
        mono: true,
        max: '12ch',
        cell: (row) => {
          const v = c.pick(row.r);
          if (v == null) return NO_VALUE;
          const lv = readingLevel(c, row.r);
          const text = v.toFixed(c.digits);
          return lv === 'warn' || lv === 'bad' ? (
            <span className={styles.ledgerBreach} data-tone={levelTone(lv)}>{text}</span>
          ) : text;
        },
      });
    }

    out.push(
      {
        key: 'seq',
        header: 'Seq',
        kind: 'num',
        mono: true,
        max: '9ch',
        drop: 5,
        cell: (row) => {
          const v = readingNum(row.r, 'seq');
          return v === null ? NO_VALUE : String(v);
        },
      },
      {
        key: 'skew',
        header: 'Offset',
        kind: 'num',
        unit: 'ms',
        mono: true,
        max: '12ch',
        drop: 5,
        cell: (row) => {
          const v = readingNum(row.r, 'clock_offset_ms');
          return v === null ? NO_VALUE : v.toFixed(2);
        },
      },
    );

    /* A column of 600 identical boot ids is wallpaper, not provenance — it
       becomes a column only once the window spans a restart. */
    if (boots > 1) {
      out.push({
        key: 'boot',
        header: 'Agent boot',
        kind: 'id',
        mono: true,
        drop: 4,
        cell: (row) => {
          const b = readingStr(row.r, 'agent_boot_id');
          return b === null ? NO_VALUE : midEllipsis(b, 12);
        },
      });
    }
    return out;
  }, [axis, crossesDay, multi, channels, boots, robotNames]);

  return (
    <Panel
      title="Readings"
      count={figure(read, num(rows.length))}
      loading={loading && rows.length === 0}
      meta={
        <span className={styles.cardMeta}>
          {/* WHICH INSTANT THIS PANE IS READING, ON THE PANE.

              The rows follow the crosshair, so the pane has to say whose
              instant it is standing on — otherwise a list scrolled to
              10:39:51Z beside a header that says nothing is a list the
              operator has to date by reading a cell. Same idiom as the
              rail's AT CURSOR eyebrow and the transport's clock: the word
              for where the instant came from, then the instant. */}
          <CursorStamp
            at={cursor.at ?? newestT}
            format={(t) => zulu(t, crossesDay)}
          />
          {/* The cursor is inside the window but more than a cadence from any
              reading — the pane has nothing to mark, and says so rather than
              tinting the nearest row and letting it be read as the answer. */}
          {cursor.at !== null && currentKey === null && rows.length > 0 && (
            <span className={styles.metaUnit} data-tone="warn">no reading here</span>
          )}
          <span className={styles.metaUnit}>{AXIS_LABEL[axis]}</span>
          <span className={styles.metaRange}>{duration(cursor.to - cursor.from)}</span>
          <span className={styles.metaUnit}>window</span>
          {cadence > 0 && (
            <>
              <span className={styles.metaRange}>{duration(cadence)}</span>
              <span className={styles.metaUnit}>cadence</span>
            </>
          )}
          {rows.length > shown.length && (
            <span className={styles.metaUnit} data-tone="warn">
              {`newest ${num(MAX_ROWS)} drawn`}
            </span>
          )}
        </span>
      }
      tools={tools}
      pad="none"
      scroll={false}
      className={`${styles.card} ${styles.ledgerCard}`}
    >
      {/* THE FAULT LINE SAYS WHY; THE ROW SAYS WHAT IS THERE. Both are owed,
          and this panel carried neither: a failed telemetry read left thirteen
          column headings over `No readings in this window.` — a table with an
          empty window, not a table whose source stopped answering. Flush to
          the panel's edge, above the headings, exactly where `ReadBody` puts
          it on every other surface. Only while nothing has landed: rows on
          screen from an earlier read are a different state and the header's
          own age carries it. */}
      {unread(read) && <FaultLine read={read} />}
      <div
        className={styles.ledgerWrap}
        ref={wrapRef}
        role="group"
        aria-label="Telemetry readings inside the transport window. Selecting a row pins the cursor on every plot."
      >
        <DataTable<LedgerRow>
          rows={shown}
          columns={columns}
          rowKey={(row) => row.key}
          currentKey={currentKey}
          sort={sort}
          onSortChange={(next) => setSort(next)}
          onRowClick={(row) => cursor.setPlayhead(row.t)}
          of={read}
          empty="No readings in this window."
        />
      </div>
    </Panel>
  );
}
